import React, { useEffect, useState, createContext, useContext } from 'react';
interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  dateOfBirth?: string;
  nin?: string;
  profileImage?: string;
}
interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  updateUserProfile?: (userData: Partial<User>) => Promise<boolean>;
  updatePassword?: (currentPassword: string, newPassword: string) => Promise<boolean>;
  updateNotificationPreferences?: (preferences: any) => Promise<boolean>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Sample user accounts with credentials
const sampleUsers = [{
  id: '123',
  name: 'Ajibola Oluga',
  email: 'ajibola@example.com',
  password: 'password123',
  phone: '08012345678',
  profileImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jnDjckdCBuR59KHCfjHFiW/1000013119.jpg'
}, {
  id: '456',
  name: 'Osaro Igbinedion',
  email: 'osaro@example.com',
  password: 'password456',
  phone: '08023456789',
  profileImage: 'https://images.unsplash.com/photo-1522529599102-193c0d76b5b6?q=80&w=250&auto=format&fit=crop'
}, {
  id: '789',
  name: 'Blessing Okonkwo',
  email: 'blessing@example.com',
  password: 'password789',
  phone: '08034567890',
  profileImage: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?q=80&w=250&auto=format&fit=crop'
}];
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);
  const login = async (email: string, password: string) => {
    // This would be an API call in a real app
    setIsLoading(true);
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Find matching user by email and password
    const matchedUser = sampleUsers.find(u => u.email.toLowerCase() === email.toLowerCase() && u.password === password);
    if (matchedUser) {
      // User found, extract password before storing
      const {
        password: _,
        ...userData
      } = matchedUser;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setIsLoading(false);
    } else {
      // For demo purposes, if no match found but email contains 'ajibola', 'osaro', or 'blessing',
      // log in as that user with any password
      if (email.toLowerCase().includes('ajibola')) {
        const {
          password: _,
          ...userData
        } = sampleUsers[0];
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else if (email.toLowerCase().includes('osaro')) {
        const {
          password: _,
          ...userData
        } = sampleUsers[1];
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else if (email.toLowerCase().includes('blessing')) {
        const {
          password: _,
          ...userData
        } = sampleUsers[2];
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        // If no match and no special case, default to Ajibola (for backward compatibility)
        const {
          password: _,
          ...userData
        } = sampleUsers[0];
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }
      setIsLoading(false);
    }
  };
  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };
  // Update user profile data
  const updateUserProfile = async (userData: Partial<User>): Promise<boolean> => {
    if (!user) return false;
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      // Update user data
      const updatedUser = {
        ...user,
        ...userData
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      return false;
    }
  };
  // Update password
  const updatePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    // Simulate password validation and update
    await new Promise(resolve => setTimeout(resolve, 800));
    // In a real app, you would validate the current password against the stored password
    // For demo purposes, we'll just return true
    return true;
  };
  // Update notification preferences
  const updateNotificationPreferences = async (preferences: any): Promise<boolean> => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 800));
    // In a real app, you would store these preferences
    return true;
  };
  return <AuthContext.Provider value={{
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isLoading,
    updateUserProfile,
    updatePassword,
    updateNotificationPreferences
  }} data-id="element-817">
      {children}
    </AuthContext.Provider>;
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};